import Plan from '@/classes/Plan'
import { OapProvider } from '@/data/oapProviders'
import { CemeteryPurchaseStatus } from '@/enums/CemeteryPurchaseStatus'
import { Disposition } from '@/enums/Disposition'
import { EducationLevelOptions } from '@/enums/EducationLevel'
import { GenderOptions } from '@/enums/Gender'
import { MaritalStatus } from '@/enums/MaritalStatus'
import { MilitaryRecord } from '@/enums/MilitaryRecord'
import { MilitaryRequests } from '@/enums/MilitaryRequests'
import { PlanEverplansPaymentType } from '@/enums/PlanEverplansPaymentType'
import { PlanOapPaymentType } from '@/enums/PlanOapPaymentType'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { PlanPaymentType } from '@/enums/PlanPaymentType'
import { RaceOptions } from '@/enums/Race'
import { ViewingOption } from '@/enums/ViewingOption'
import type {
	CreditCard,
	DirectBill,
	EFT,
	InmanOapPayment,
	PlanData,
	SepioOapPayment,
} from '@/types'
import { faker } from '@faker-js/faker'
import { PaymentType, Term } from '@precoa/calculator-sdk'

export function EftFactory(properties: Partial<EFT> = {}): EFT {
	return {
		accountHolder: faker.person.fullName(),
		bankName: faker.company.name(),
		routingNumber: faker.string.numeric(9),
		accountNumber: faker.string.numeric(9),
		accountType: faker.helpers.arrayElement(['checking', 'savings']),
		...properties,
	}
}

export function CreditCardFactory(): CreditCard {
	return {
		token: faker.string.uuid(),
	}
}

export function DirectBillFactory(): DirectBill {
	return {
		billingType: faker.helpers.arrayElement([
			'Monthly',
			'Quarterly',
			'Semi-Annual',
			'Annual',
		]),
	}
}

export function SepioPaymentFactory(): SepioOapPayment {
	return {
		paymentPlanId: faker.string.uuid(),
		provider: OapProvider.SEPIO_GUARD,
		paymentPlan: {
			// SepioPaymentDetails
			paymentType: PlanOapPaymentType.EFT,
			paymentDetails: EftFactory({ bankName: '' }),
		},
	}
}

export function InmanPaymentFactory(): InmanOapPayment {
	return {
		paymentPlanId: faker.string.uuid(),
		provider: OapProvider.INMAN,
		paymentPlan: {
			paymentDayOfMonth: faker.number.int({ min: 1, max: 28 }),
			confirmed: faker.datatype.boolean(),
			planType: faker.helpers.arrayElement(['Individual', 'Couple']),
		},
	}
}

export default function PlanFactory(
	properties: Partial<PlanData> = {}
): PlanData {
	return {
		id: faker.string.uuid(),
		funeralHomeId: faker.string.uuid(),
		leadId: faker.string.uuid(),
		opportunityId: faker.string.alphanumeric(18),
		firstName: faker.person.firstName(),
		middleName: faker.person.middleName(),
		lastName: faker.person.lastName(),
		maidenName: faker.person.lastName(),
		address: faker.location.streetAddress(),
		city: faker.location.city(),
		state: faker.location.state({ abbreviated: true }),
		zip: faker.location.zipCode(),
		email: faker.internet.email(),
		phone: faker.phone.number(),
		secondaryPhone: faker.phone.number(),
		birthDate: faker.date.birthdate().toLocaleDateString(),
		birthCity: faker.location.city(),
		birthState: faker.location.state({ abbreviated: true }),
		birthCounty: faker.location.county(),
		birthCountry: faker.location.country(),
		educationLevel: faker.helpers.arrayElement(
			Object.values(EducationLevelOptions)
		),
		occupation: faker.person.jobTitle(),
		occupationYears: faker.string.numeric(),
		businessType: faker.company.name(),
		race: faker.helpers.arrayElement(Object.values(RaceOptions)),
		gender: faker.helpers.arrayElement(Object.values(GenderOptions)),
		maritalStatus: faker.helpers.arrayElement(Object.values(MaritalStatus)),
		vitalInfoNotes: faker.lorem.sentence(),

		ownerIsInsured: faker.datatype.boolean(),
		socialSecurityNumber: faker.string.numeric(9),
		beneficiary: faker.person.fullName(),
		beneficiaryRelationship: faker.string.fromCharacters([
			'Spouse',
			'Parent',
			'Sibling',
			'Friend',
		]),
		completedSteps: faker.helpers.uniqueArray(
			['confirmation', 'Payment Authorization'],
			2
		),
		documents: [],
		policyOwner: {
			firstName: faker.person.firstName(),
			middleName: faker.person.middleName(),
			lastName: faker.person.lastName(),
			birthDate: faker.date.birthdate().toLocaleDateString(),
			socialSecurityNumber: faker.string.numeric(9),
			address: faker.location.streetAddress(),
			city: faker.location.city(),
			state: faker.location.state({ abbreviated: true }),
			zip: faker.location.zipCode(),
			email: faker.internet.email(),
			phone: faker.phone.number(),
			secondaryPhone: faker.phone.number(),
		},
		payment: {
			paymentType: PlanPaymentType.EFT,
			paymentDetails: {
				initialAccount: EftFactory({ accountHolder: '' }),
				ongoingAccount: EftFactory({ accountHolder: '' }),
				useDifferentOngoingAccount: false,
			},
		},

		oapPayment: faker.helpers.arrayElement([
			SepioPaymentFactory(),
			InmanPaymentFactory(),
		]),
		everplansPayment: {
			amount: 0,
			paymentType: PlanEverplansPaymentType.EFT,
			paymentDetails: EftFactory(),
		},
		paymentStep: 'html',
		oapStep: 'html',
		everplansStep: 'html',
		packetStatus: PlanPacketStatus.Incomplete,

		spouseInfo: {
			firstName: faker.person.firstName(),
			middleName: faker.person.middleName(),
			maidenName: faker.person.lastName(),
			lastName: faker.person.lastName(),
			marriageDate: faker.date.past().toLocaleDateString(),
			marriageCity: faker.location.city(),
			marriageState: faker.location.state({ abbreviated: true }),
			marriageCounty: faker.location.county(),
		},
		fatherInfo: {
			firstName: faker.person.firstName(),
			middleName: faker.person.middleName(),
			lastName: faker.person.lastName(),
			city: faker.location.city(),
			state: faker.location.state({ abbreviated: true }),
			birthCounty: faker.location.county(),
			birthCountry: faker.location.country(),
		},
		motherInfo: {
			firstName: faker.person.firstName(),
			middleName: faker.person.middleName(),
			maidenName: faker.person.lastName(),
			city: faker.location.city(),
			state: faker.location.state({ abbreviated: true }),
			birthCounty: faker.location.county(),
			birthCountry: faker.location.country(),
		},
		parentalInfoNotes: faker.lorem.sentence(),
		militaryRecordNotes: faker.lorem.sentence(),
		militaryRecord: faker.helpers.arrayElements(Object.values(MilitaryRecord)),
		militaryRequests: faker.helpers.arrayElements(
			Object.values(MilitaryRequests)
		),
		peopleResponsible: [
			{
				firstName: faker.person.firstName(),
				lastName: faker.person.lastName(),
				relationship: faker.helpers.arrayElement([
					'Spouse',
					'Parent',
					'Sibling',
					'Friend',
				]),
				city: faker.location.city(),
				state: faker.location.state({ abbreviated: true }),
				zip: faker.location.zipCode(),
				phone: faker.phone.number(),
				email: faker.internet.email(),
				notes: faker.lorem.sentence(),
			},
		],

		// Service Preferences
		funeralHomeName: faker.helpers.arrayElement([
			'Sunset Funeral Home',
			'Greenlawn Funeral Home',
			'Sytsema Funeral Home',
		]),
		placeOfService: faker.location.streetAddress(),
		placeOfGathering: faker.location.streetAddress(),
		religiousPreference: faker.helpers.arrayElement([
			'Catholic',
			'Christian',
			'Hindu',
			'Muslim',
		]),
		serviceOrganization: faker.helpers.arrayElement([
			'Military',
			'Fraternal',
			'Lodge',
			'Union',
		]),
		serviceNotes: faker.lorem.sentence(),

		postServiceMeal: faker.datatype.boolean(),
		postServiceMealDetails: faker.lorem.sentence(),
		viewingPreference: faker.helpers.arrayElement(Object.values(ViewingOption)),
		burialOrCremation: faker.helpers.arrayElement(Object.values(Disposition)),
		intermentPrefs: faker.helpers.arrayElement([
			'Mausoleum',
			'Lawn Crypt',
			'Ground Burial',
			'Burial',
			'Niche',
			'Family Pick-up',
			'Scatter',
		]),
		cemeteryPurchaseStatus: faker.helpers.arrayElement(
			Object.values(CemeteryPurchaseStatus)
		),
		cemeteryPropertyOwner: faker.person.fullName(),
		cemeteryName: faker.helpers.arrayElement([
			'Serene Meadows Memorial Park',
			'Whispering Oaks Burial Grounds',
			'Hilltop Preserve',
			'Cedar Grove Cemetery',
		]),
		intermentCity: faker.location.city(),
		intermentState: faker.location.state({ abbreviated: true }),
		intermentNotes: faker.lorem.sentence(5),
		specialInstructions: {
			flowerType: faker.helpers.arrayElement([
				'Rose',
				'Lily',
				'Tulip',
				'Carnation',
				'Daisy',
				'Other',
			]),
			colorPreferences: faker.helpers.arrayElement([
				'Red',
				'Orange',
				'Yellow',
				'Green',
				'Blue',
				'Purple',
				'Pink',
				'Brown',
				'Black',
				'White',
				'Other',
			]),
			musicPreference: faker.helpers.arrayElement([
				'Jazz',
				'Rock',
				'Country',
				'Pop',
				'Classical',
				'Other',
			]),
			casketBearers: faker.person.fullName(),
			wantsToNotifyBearers: faker.datatype.boolean(),
			obitPublications: faker.helpers.arrayElement([
				'New York Times',
				'Guardian',
				'Funeral.com',
			]),
			wantsObitPhoto: faker.datatype.boolean(),
		},

		accessoriesInstructions: [
			{
				item: faker.helpers.arrayElement([
					'Pearl Necklace',
					'Diamond Ring',
					'Gold Earrings',
					'Silver Bracelet',
					'Gold Chain',
				]),
				returnTo: faker.person.fullName(),
			},
		],
		otherMemorialNotes: faker.lorem.sentence(5),

		mostImportantReason: faker.lorem.sentence(),
		cartItems: [],
		oapIncluded: faker.datatype.boolean(),
		everplansIncluded: faker.datatype.boolean(),
		everplansEmail: faker.internet.email(),
		customCartItems: [],
		lastUpdated: new Date().toISOString(),
		errors: [],

		insurance: {
			paymentType: PaymentType.MP_EFT,
			terminallyIll: false,
			otherIllness: false,
		},

		insuranceScenarioOutput: {
			planTerm: Term.SinglePay,
			totalCost: 1000,
			paidToday: 800,
			firstPayment: 0,
			downPayment: 200,
			paymentSummary: [],
		},

		...properties,
	}
}

export function PlanWithDocumentsFactory(
	properties: Partial<PlanData> = {},
	docs: [any]
) {
	const planData = PlanFactory(properties)
	return {
		...planData,
		documents: docs.map((doc) => ({ id: doc.id, type: doc.meta.type })),
	}
}

// TODO: Update to use StoredPlanData type for planData
export function CloudSyncPlanFactory(plan?: PlanData) {
	const planData = plan ?? new Plan(PlanFactory())
	return {
		id: planData.id,
		crmLeadId: planData.leadId,
		encryptionMetadata: EncryptionMetadataFactory(),
		lastUpdated: planData.lastUpdated,
		planData: StoredPlanDataFactory(planData),
		salesforceAccountPersonAccountId: planData.contactId,
		salesforceOpportunityId: planData?.opportunityId,
	}
}

export function EncryptionMetadataFactory() {
	return {
		keyId: faker.string.uuid(),
		secretEncodedKey: faker.lorem.sentence(),
		secretSalt: faker.string.uuid(),
		serverEncodedKey: faker.string.uuid(),
		serverKeyId: faker.string.uuid(),
	}
}

export function StoredPlanDataFactory(plan?: PlanData) {
	const planData = plan ?? new Plan(PlanFactory())

	return {
		version: faker.number.int({ min: 1, max: 2 }),
		basic: planData, //TODO: only get basic
		vault: null, //TODO: encrypt?
	}
}
